import { useCallback, useMemo, useState } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { listChallengeHistory } from "../../challenges/services/challengeAdminService";
import type { ChallengeHistoryItem } from "../../challenges/types/challengeAdmin";
import {
  deleteEventChallengeLink,
  listEventChallengeLinks,
  upsertEventChallengeLink,
  type AdminEventChallengeLink,
} from "../services/eventRegistrationAdminService";

type UseAdminEventChallengeLinksParams = {
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminEventChallengeLinks({
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminEventChallengeLinksParams) {
  const t = siteTexts.admin;
  const [eventChallengeLinks, setEventChallengeLinks] = useState<
    Record<string, AdminEventChallengeLink>
  >({});
  const [eventChallengeLinksLoading, setEventChallengeLinksLoading] =
    useState(false);
  const [eventChallengeLinkDrafts, setEventChallengeLinkDrafts] = useState<
    Record<string, string>
  >({});
  const [linkableEventChallenges, setLinkableEventChallenges] = useState<
    ChallengeHistoryItem[]
  >([]);

  const loadEventLinks = useCallback(async () => {
    if (!token) return;
    setEventChallengeLinksLoading(true);
    try {
      const [links, challenges] = await Promise.all([
        listEventChallengeLinks(token),
        listChallengeHistory(token, { status: "all" }),
      ]);
      const nextMap: Record<string, AdminEventChallengeLink> = {};
      const nextDrafts: Record<string, string> = {};
      links.forEach((link) => {
        nextMap[link.eventId] = link;
        nextDrafts[link.eventId] = String(link.tournamentId);
      });
      setEventChallengeLinks(nextMap);
      setEventChallengeLinkDrafts((prev) => ({ ...prev, ...nextDrafts }));
      setLinkableEventChallenges(challenges);
    } catch (_err) {
      setEventChallengeLinks({});
      setLinkableEventChallenges([]);
    } finally {
      setEventChallengeLinksLoading(false);
    }
  }, [token]);

  const onUpsertEventLink = async (eventId: string) => {
    if (!token) return;
    const raw = String(eventChallengeLinkDrafts[eventId] || "").trim();
    const tournamentId = Number(raw);
    if (!Number.isInteger(tournamentId) || tournamentId <= 0) {
      showErrorToast(t.validation.invalidChallengeId);
      return;
    }
    try {
      await upsertEventChallengeLink(token, eventId, tournamentId);
      await loadEventLinks();
      showSuccessToast(t.eventLink.toasts.saved);
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : t.eventLink.toasts.saveError,
      );
    }
  };

  const onDeleteEventLink = async (eventId: string) => {
    if (!token) return;
    try {
      await deleteEventChallengeLink(token, eventId);
      await loadEventLinks();
      showSuccessToast(t.eventLink.toasts.removed);
    } catch (_err) {
      showErrorToast(t.eventLink.toasts.removeError);
    }
  };

  const linkableEventChallengesSorted = useMemo(() => {
    const list = [...linkableEventChallenges];
    list.sort((a, b) => {
      const aKey = String(a.scheduledAt || a.createdAt || "");
      const bKey = String(b.scheduledAt || b.createdAt || "");
      return bKey.localeCompare(aKey);
    });
    return list.slice(0, 80);
  }, [linkableEventChallenges]);

  return {
    eventChallengeLinkDrafts,
    eventChallengeLinks,
    eventChallengeLinksLoading,
    linkableEventChallengesSorted,
    loadEventLinks,
    onDeleteEventLink,
    onUpsertEventLink,
    setEventChallengeLinkDrafts,
    setEventChallengeLinks,
  };
}
